import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch, ApiRequestError } from "../lib/api.js";
import { formatTimestamp } from "../lib/formatTime.js";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { useToast } from "../components/Toast.js";
import { useConfirm } from "../components/ConfirmDialog.js";
import {
  INTEGRATION_KINDS,
  INTEGRATION_KIND_LIST,
  type TenantIntegrationKind
} from "../lib/integrationKinds.js";

/**
 * Tenant admin — Integrations list (ADR-0085 Phase 2 §5.2).
 *
 * Route: `/app/admin/integrations`. Kind-agnostic list. The toolbar exposes a
 * kind filter when more than one kind is registered.
 */

interface IntegrationListItem {
  id: string;
  name: string;
  kind: TenantIntegrationKind;
  config: unknown;
  last_test_at: string | null;
  last_test_status: "success" | "failed" | null;
  last_test_error: string | null;
  created_at: string;
  updated_at: string;
  updated_by_email: string | null;
  used_by_count: number;
}

export function Integrations(): JSX.Element {
  const toast = useToast();
  const { confirm } = useConfirm();

  const [items, setItems] = useState<IntegrationListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [kindFilter, setKindFilter] = useState<"" | TenantIntegrationKind>("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = kindFilter ? `?kind=${kindFilter}` : "";
      const res = await apiFetch<{ integrations: IntegrationListItem[] }>(`/api/tenant/admin/integrations${qs}`);
      setItems(res.integrations);
    } catch (e) {
      setError(e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [kindFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  async function deleteIntegration(i: IntegrationListItem) {
    const ok = await confirm({
      title: `Delete "${i.name}"?`,
      detail: "Anyone using this integration will fail until it's recreated.",
      confirmLabel: "Delete"
    });
    if (!ok) return;
    setBusy((p) => ({ ...p, [i.id]: true }));
    try {
      await apiFetch(`/api/tenant/admin/integrations/${i.id}`, { method: "DELETE" });
      toast.push({ kind: "success", message: "Integration deleted." });
      await load();
    } catch (e) {
      const msg = e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e);
      toast.push({ kind: "error", message: msg });
    } finally {
      setBusy((p) => ({ ...p, [i.id]: false }));
    }
  }

  return (
    <PageLayout title="Integrations">
      <div style={{ display: "grid", gap: 12 }}>
        <div className="card adminList">
          <div className="toolbar">
            <Link to="/app/admin/integrations/new" className="adminNewLink">
              + New integration
            </Link>
            {INTEGRATION_KIND_LIST.length > 1 ? (
              <div className="toolbarGroup" style={{ marginLeft: "auto" }}>
                <label className="field" style={{ display: "flex", alignItems: "center", gap: 6, margin: 0 }}>
                  <span className="label" style={{ margin: 0 }}>Kind</span>
                  <select
                    className="select"
                    value={kindFilter}
                    onChange={(e) => setKindFilter(e.target.value as "" | TenantIntegrationKind)}
                  >
                    <option value="">All</option>
                    {INTEGRATION_KIND_LIST.map((k) => (
                      <option key={k.kind} value={k.kind}>
                        {k.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            ) : null}
          </div>
          {error ? (
            <ErrorState detail={error} />
          ) : loading ? (
            <LoadingState />
          ) : items.length === 0 ? (
            <div className="adminListEmpty">No integrations configured.</div>
          ) : (
            <div className="tableWrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Kind</th>
                    <th>Last test</th>
                    <th>Used by</th>
                    <th>Updated</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {items.map((i) => (
                    <IntegrationRow
                      key={i.id}
                      item={i}
                      busy={Boolean(busy[i.id])}
                      onDelete={() => void deleteIntegration(i)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </PageLayout>
  );
}

function IntegrationRow(props: {
  item: IntegrationListItem;
  busy: boolean;
  onDelete: () => void;
}): JSX.Element {
  const { item, busy, onDelete } = props;
  const meta = INTEGRATION_KINDS[item.kind];
  return (
    <tr>
      <td>
        <Link to={`/app/admin/integrations/${item.id}`} className="adminListName">
          {item.name}
        </Link>
      </td>
      <td>
        <span className="pill">{meta?.label ?? item.kind}</span>
      </td>
      <td>
        {item.last_test_status === "success" ? (
          <span style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
            <span className="pill pillOk">success</span>
            <span className="muted">{formatTimestamp(item.last_test_at)}</span>
          </span>
        ) : item.last_test_status === "failed" ? (
          <span style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
            <span className="pill pillBad" title={item.last_test_error ?? ""}>failed</span>
            <span className="muted">{formatTimestamp(item.last_test_at)}</span>
          </span>
        ) : (
          <span className="muted">never</span>
        )}
      </td>
      <td>
        {item.used_by_count > 0 ? (
          <span className="pill" title={`${item.used_by_count} dependent(s)`}>
            {item.used_by_count}
          </span>
        ) : (
          <span className="muted">0</span>
        )}
      </td>
      <td className="muted">{formatTimestamp(item.updated_at)}</td>
      <td>
        <div className="adminListActions">
          <Link to={`/app/admin/integrations/${item.id}`} className="adminListAction">
            Edit
          </Link>
          <button
            type="button"
            className="adminListAction isDanger"
            onClick={onDelete}
            disabled={busy || item.used_by_count > 0}
            title={item.used_by_count > 0 ? "In use — remove dependents first" : undefined}
          >
            Delete
          </button>
        </div>
      </td>
    </tr>
  );
}
